import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ArrowLeft, Plus, DollarSign, Users, TrendingUp, Activity,
  FileText, BarChart3, Trash2, PieChart
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHART_COLORS = ["#1e3a8a", "#2563eb", "#60a5fa"];
const CHART_TICK = { fontSize: 11, fontFamily: "IBM Plex Sans" };
const TOOLTIP_STYLE = { fontSize: 12, fontFamily: "IBM Plex Sans" };

const fmt = (n) => {
  if (n == null) return "$0";
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(0)}K`;
  return `${sign}$${abs.toFixed(0)}`;
};

const fmtNum = (n) => n != null ? n.toLocaleString() : "0";

export default function BusinessDetailPage() {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [financials, setFinancials] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/businesses/${businessId}/financials`, { withCredentials: true });
      setBusiness(data.business);
      setFinancials(data.financials);
      if (data.financials.length > 0) {
        setSelectedPeriod((prev) => prev || data.financials[data.financials.length - 1].period);
      }
    } catch (e) {
      if (e.response?.status === 401) navigate("/login");
    } finally {
      setLoading(false);
    }
  }, [businessId, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const deletePeriod = async (fid) => {
    try {
      await axios.delete(`${API}/businesses/${businessId}/financials/${fid}`, { withCredentials: true });
      toast.success("Period deleted");
      fetchData();
    } catch { toast.error("Failed to delete"); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-blue-900 border-t-transparent rounded-full animate-spin" /></div>;

  const current = financials.find((f) => f.period === selectedPeriod) || financials[financials.length - 1];
  const latest = financials[financials.length - 1];

  return (
    <div className="min-h-screen bg-white font-ibm" data-testid="business-detail-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-[1440px] mx-auto px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard" className="text-slate-500 hover:text-slate-800 h-8 px-2">
              <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
            </Button>
            <Separator orientation="vertical" className="h-5" />
            <h1 className="font-manrope font-bold text-sm text-[#0f172a] truncate max-w-[300px]" data-testid="business-name">
              {business?.name || "Business"}
            </h1>
            {business?.sector && <Badge variant="outline" className="text-xs shrink-0">{business.sector}</Badge>}
          </div>
          <div className="flex items-center gap-3">
            {financials.length > 0 && (
              <Select value={selectedPeriod || ""} onValueChange={setSelectedPeriod}>
                <SelectTrigger className="h-8 w-[120px] text-xs" data-testid="period-selector">
                  <SelectValue placeholder="Period" />
                </SelectTrigger>
                <SelectContent>
                  {financials.map((f) => (
                    <SelectItem key={f.period} value={f.period} className="text-xs">{f.period}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="h-8 text-xs bg-[#1e3a8a] hover:bg-[#1e40af]" data-testid="add-period-button">
                  <Plus className="w-3 h-3 mr-1" /> Add Period
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[85vh]">
                <DialogHeader>
                  <DialogTitle className="font-manrope">Add Financial Period</DialogTitle>
                </DialogHeader>
                <AddPeriodForm businessId={businessId} onSuccess={() => { setAddOpen(false); fetchData(); }} />
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      <main className="max-w-[1440px] mx-auto px-6 py-6">
        {/* Summary Cards */}
        {latest && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6" data-testid="financial-summary-cards">
            <MiniCard icon={DollarSign} label="Revenue" value={fmt(latest.revenue)} testId="summary-revenue" />
            <MiniCard icon={TrendingUp} label="Gross Profit" value={fmt(latest.gross_profit)} testId="summary-gp" />
            <MiniCard icon={Activity} label="EBITDA" value={fmt(latest.ebitda)} testId="summary-ebitda" />
            <MiniCard icon={FileText} label="Net Income" value={fmt(latest.net_income)} accent testId="summary-ni" />
            <MiniCard icon={Users} label="Customers" value={fmtNum(latest.total_customers)} testId="summary-customers" />
            <MiniCard icon={BarChart3} label="MRR" value={fmt(latest.mrr)} testId="summary-mrr" />
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="financials" className="space-y-4" data-testid="business-tabs">
          <TabsList className="bg-slate-100 p-1">
            <TabsTrigger value="financials" data-testid="tab-financials" className="text-xs font-manrope font-semibold data-[state=active]:bg-white">
              <FileText className="w-3.5 h-3.5 mr-1.5" /> Financial Statements
            </TabsTrigger>
            <TabsTrigger value="revenue" data-testid="tab-revenue" className="text-xs font-manrope font-semibold data-[state=active]:bg-white">
              <BarChart3 className="w-3.5 h-3.5 mr-1.5" /> Revenue & Growth
            </TabsTrigger>
            <TabsTrigger value="customers" data-testid="tab-customers" className="text-xs font-manrope font-semibold data-[state=active]:bg-white">
              <Users className="w-3.5 h-3.5 mr-1.5" /> Customers & KPIs
            </TabsTrigger>
          </TabsList>

          {/* Financial Statements Tab */}
          <TabsContent value="financials" data-testid="financials-tab-content">
            {current ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Income Statement */}
                <Card className="border-slate-200" data-testid="income-statement-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-manrope font-semibold text-slate-700">Income Statement</CardTitle>
                    <p className="text-xs text-slate-400 font-ibm">{current.period}</p>
                  </CardHeader>
                  <CardContent className="space-y-1.5">
                    <FinLine label="Revenue" value={current.revenue} bold />
                    <FinLine label="Cost of Revenue" value={-current.cost_of_revenue} />
                    <Separator className="my-2" />
                    <FinLine label="Gross Profit" value={current.gross_profit} bold accent />
                    <FinLine label="Operating Expenses" value={-current.operating_expenses} />
                    <Separator className="my-2" />
                    <FinLine label="EBITDA" value={current.ebitda} bold />
                    <FinLine label="Depreciation" value={-current.depreciation} />
                    <FinLine label="Interest Expense" value={-current.interest_expense} />
                    <FinLine label="Tax Expense" value={-current.tax_expense} />
                    <Separator className="my-2" />
                    <FinLine label="Net Income" value={current.net_income} bold accent />
                  </CardContent>
                </Card>

                {/* Balance Sheet */}
                <Card className="border-slate-200" data-testid="balance-sheet-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-manrope font-semibold text-slate-700">Balance Sheet</CardTitle>
                    <p className="text-xs text-slate-400 font-ibm">{current.period}</p>
                  </CardHeader>
                  <CardContent className="space-y-1.5">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold mt-1">Assets</p>
                    <FinLine label="Cash & Equivalents" value={current.cash_equivalents} />
                    <FinLine label="Accounts Receivable" value={current.accounts_receivable} />
                    <FinLine label="Inventory" value={current.inventory} />
                    <FinLine label="Current Assets" value={current.current_assets} bold />
                    <FinLine label="Fixed Assets" value={current.fixed_assets} />
                    <Separator className="my-2" />
                    <FinLine label="Total Assets" value={current.total_assets} bold accent />
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold mt-3">Liabilities & Equity</p>
                    <FinLine label="Current Liabilities" value={current.current_liabilities} />
                    <FinLine label="Long-term Debt" value={current.long_term_debt} />
                    <FinLine label="Total Liabilities" value={current.total_liabilities} bold />
                    <Separator className="my-2" />
                    <FinLine label="Total Equity" value={current.total_equity} bold accent />
                  </CardContent>
                </Card>

                {/* Cash Flow */}
                <Card className="border-slate-200" data-testid="cash-flow-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-manrope font-semibold text-slate-700">Cash Flow Statement</CardTitle>
                    <p className="text-xs text-slate-400 font-ibm">{current.period}</p>
                  </CardHeader>
                  <CardContent className="space-y-1.5">
                    <FinLine label="Operating Cash Flow" value={current.operating_cash_flow} />
                    <FinLine label="Investing Cash Flow" value={current.investing_cash_flow} />
                    <FinLine label="Financing Cash Flow" value={current.financing_cash_flow} />
                    <Separator className="my-2" />
                    <FinLine label="Net Cash Flow" value={current.net_cash_flow} bold accent />
                    <div className="mt-6 pt-4 border-t border-slate-100">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold mb-2">Key Ratios</p>
                      <div className="grid grid-cols-2 gap-3">
                        <RatioItem label="Gross Margin" value={current.revenue ? `${(current.gross_profit / current.revenue * 100).toFixed(1)}%` : "N/A"} />
                        <RatioItem label="EBITDA Margin" value={current.revenue ? `${(current.ebitda / current.revenue * 100).toFixed(1)}%` : "N/A"} />
                        <RatioItem label="Net Margin" value={current.revenue ? `${(current.net_income / current.revenue * 100).toFixed(1)}%` : "N/A"} />
                        <RatioItem label="Debt/Equity" value={current.total_equity ? `${(current.total_liabilities / current.total_equity).toFixed(2)}x` : "N/A"} />
                        <RatioItem label="Current Ratio" value={current.current_liabilities ? `${(current.current_assets / current.current_liabilities).toFixed(2)}x` : "N/A"} />
                        <RatioItem label="ROE" value={current.total_equity ? `${(current.net_income / current.total_equity * 100).toFixed(1)}%` : "N/A"} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <EmptyState message="No financial data yet. Add your first period." />
            )}

            {/* Period History */}
            {financials.length > 0 && (
              <Card className="border-slate-200 mt-4" data-testid="period-history">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-manrope font-semibold text-slate-700">Period History</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs font-ibm">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="text-left px-4 py-2 font-semibold text-slate-500 uppercase tracking-wider">Period</th>
                          <th className="text-right px-4 py-2 font-semibold text-slate-500 uppercase tracking-wider">Revenue</th>
                          <th className="text-right px-4 py-2 font-semibold text-slate-500 uppercase tracking-wider">EBITDA</th>
                          <th className="text-right px-4 py-2 font-semibold text-slate-500 uppercase tracking-wider">Net Income</th>
                          <th className="text-right px-4 py-2 font-semibold text-slate-500 uppercase tracking-wider">Assets</th>
                          <th className="text-right px-4 py-2 font-semibold text-slate-500 uppercase tracking-wider">Customers</th>
                          <th className="w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {financials.map((f) => (
                          <tr key={f.id} className={`border-b border-slate-100 hover:bg-slate-50/50 cursor-pointer ${f.period === selectedPeriod ? "bg-blue-50/50" : ""}`} onClick={() => setSelectedPeriod(f.period)} data-testid={`period-row-${f.period}`}>
                            <td className="px-4 py-2.5 font-medium text-slate-800">{f.period}</td>
                            <td className="px-4 py-2.5 text-right font-ibm-mono">{fmt(f.revenue)}</td>
                            <td className="px-4 py-2.5 text-right font-ibm-mono">{fmt(f.ebitda)}</td>
                            <td className={`px-4 py-2.5 text-right font-ibm-mono ${f.net_income >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmt(f.net_income)}</td>
                            <td className="px-4 py-2.5 text-right font-ibm-mono">{fmt(f.total_assets)}</td>
                            <td className="px-4 py-2.5 text-right font-ibm-mono">{fmtNum(f.total_customers)}</td>
                            <td className="px-2">
                              <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-300 hover:text-red-500" onClick={(e) => { e.stopPropagation(); deletePeriod(f.id); }} data-testid={`delete-period-${f.period}`}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Revenue & Growth Tab */}
          <TabsContent value="revenue" data-testid="revenue-tab-content">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Revenue Trend */}
              <Card className="border-slate-200" data-testid="revenue-trend-chart">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-manrope font-semibold text-slate-700">Revenue Trend</CardTitle>
                </CardHeader>
                <CardContent>
                  {financials.length > 0 ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={financials.map((f) => ({ period: f.period, Revenue: f.revenue, EBITDA: f.ebitda, "Net Income": f.net_income }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="period" tick={CHART_TICK} />
                        <YAxis tick={CHART_TICK} tickFormatter={(v) => fmt(v)} />
                        <Tooltip formatter={(v) => fmt(v)} contentStyle={TOOLTIP_STYLE} />
                        <Bar dataKey="Revenue" fill="#1e3a8a" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="EBITDA" fill="#2563eb" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="Net Income" fill="#60a5fa" radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <EmptyState message="No data available" />}
                </CardContent>
              </Card>

              {/* Revenue Streams */}
              <Card className="border-slate-200" data-testid="revenue-streams-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-manrope font-semibold text-slate-700">Revenue Streams ({current?.period || "N/A"})</CardTitle>
                </CardHeader>
                <CardContent>
                  {current?.revenue_streams?.length > 0 ? (
                    <div className="space-y-4">
                      {current.revenue_streams.map((s, i) => (
                        <div key={s.name} data-testid={`revenue-stream-${i}`}>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm font-ibm text-slate-700">{s.name}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-ibm-mono font-medium">{fmt(s.amount)}</span>
                              <Badge className="text-[10px] bg-emerald-50 text-emerald-700 border-0">+{s.growth_pct}%</Badge>
                            </div>
                          </div>
                          <Progress value={current.revenue ? (s.amount / current.revenue * 100) : 0} className="h-2" />
                        </div>
                      ))}
                      <Separator className="my-3" />
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-ibm text-slate-800 font-semibold">Total Revenue</span>
                        <span className="text-sm font-ibm-mono font-bold text-slate-800">{fmt(current.revenue)}</span>
                      </div>
                    </div>
                  ) : <EmptyState message="No revenue stream data" />}
                </CardContent>
              </Card>

              {/* Profitability Metrics */}
              <Card className="border-slate-200 lg:col-span-2" data-testid="profitability-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-manrope font-semibold text-slate-700">Profitability Across Periods</CardTitle>
                </CardHeader>
                <CardContent>
                  {financials.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={financials.map((f) => ({
                        period: f.period,
                        "Gross Margin": f.revenue ? +(f.gross_profit / f.revenue * 100).toFixed(1) : 0,
                        "EBITDA Margin": f.revenue ? +(f.ebitda / f.revenue * 100).toFixed(1) : 0,
                        "Net Margin": f.revenue ? +(f.net_income / f.revenue * 100).toFixed(1) : 0,
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="period" tick={CHART_TICK} />
                        <YAxis tick={CHART_TICK} tickFormatter={(v) => `${v}%`} />
                        <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => `${v}%`} />
                        <Area type="monotone" dataKey="Gross Margin" stroke="#1e3a8a" fill="#1e3a8a" fillOpacity={0.15} strokeWidth={2} />
                        <Area type="monotone" dataKey="EBITDA Margin" stroke="#2563eb" fill="#2563eb" fillOpacity={0.1} strokeWidth={2} />
                        <Area type="monotone" dataKey="Net Margin" stroke="#60a5fa" fill="#60a5fa" fillOpacity={0.05} strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : <EmptyState message="No data available" />}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Customers & KPIs Tab */}
          <TabsContent value="customers" data-testid="customers-tab-content">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Customer Trend */}
              <Card className="border-slate-200 lg:col-span-2" data-testid="customer-trend-chart">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-manrope font-semibold text-slate-700">Customer Growth</CardTitle>
                </CardHeader>
                <CardContent>
                  {financials.length > 0 ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <AreaChart data={financials.map((f) => ({ period: f.period, Customers: f.total_customers, "New Customers": f.new_customers }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="period" tick={CHART_TICK} />
                        <YAxis tick={CHART_TICK} />
                        <Tooltip contentStyle={TOOLTIP_STYLE} />
                        <Area type="monotone" dataKey="Customers" stroke="#1e3a8a" fill="#1e3a8a" fillOpacity={0.12} strokeWidth={2} />
                        <Area type="monotone" dataKey="New Customers" stroke="#60a5fa" fill="#60a5fa" fillOpacity={0.08} strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : <EmptyState message="No data available" />}
                </CardContent>
              </Card>

              {/* Customer Metrics */}
              <Card className="border-slate-200" data-testid="customer-metrics-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-manrope font-semibold text-slate-700">Customer Metrics ({current?.period || "N/A"})</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {current ? (
                    <>
                      <MetricRow label="Total Customers" value={fmtNum(current.total_customers)} />
                      <MetricRow label="New Customers" value={fmtNum(current.new_customers)} accent />
                      <MetricRow label="Churned" value={fmtNum(current.churned_customers)} danger />
                      <MetricRow label="Churn Rate" value={`${current.churn_rate}%`} />
                      <Separator />
                      <MetricRow label="CAC" value={fmt(current.cac)} />
                      <MetricRow label="LTV" value={fmt(current.ltv)} />
                      <MetricRow label="LTV:CAC" value={current.cac ? `${(current.ltv / current.cac).toFixed(1)}x` : "N/A"} accent />
                      <Separator />
                      <MetricRow label="MRR" value={fmt(current.mrr)} />
                      <MetricRow label="ARR" value={fmt(current.arr)} bold />
                    </>
                  ) : <EmptyState message="No data" />}
                </CardContent>
              </Card>

              {/* KPIs */}
              <Card className="border-slate-200 lg:col-span-3" data-testid="kpis-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-manrope font-semibold text-slate-700">Key Performance Indicators ({current?.period || "N/A"})</CardTitle>
                </CardHeader>
                <CardContent>
                  {current?.kpis?.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                      {current.kpis.map((kpi, i) => (
                        <div key={kpi.name} className="p-4 border border-slate-200 rounded-sm" data-testid={`kpi-${i}`}>
                          <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold mb-1">{kpi.name}</p>
                          <p className="text-xl font-manrope font-bold text-slate-800">{kpi.value}{kpi.unit}</p>
                          <div className="mt-2">
                            <div className="flex justify-between text-[10px] text-slate-400 mb-1">
                              <span>Progress</span>
                              <span>Target: {kpi.target}{kpi.unit}</span>
                            </div>
                            <Progress value={Math.min(100, (kpi.value / kpi.target) * 100)} className="h-1.5" />
                          </div>
                          <p className={`text-[10px] mt-1 font-semibold ${kpi.value >= kpi.target ? "text-emerald-600" : "text-amber-600"}`}>
                            {kpi.value >= kpi.target ? "On Target" : `${((kpi.target - kpi.value)).toFixed(1)}${kpi.unit} below target`}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : <EmptyState message="No KPIs configured" />}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

/* ── Sub Components ── */

function MiniCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <Card className="border-slate-200" data-testid={testId}>
      <CardContent className="p-3 flex items-center gap-2.5">
        <div className={`w-8 h-8 rounded-sm flex items-center justify-center shrink-0 ${accent ? "bg-emerald-50" : "bg-slate-100"}`}>
          <Icon className={`w-3.5 h-3.5 ${accent ? "text-emerald-600" : "text-[#1e3a8a]"}`} />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] text-slate-400 font-ibm uppercase tracking-wider">{label}</p>
          <p className={`text-sm font-manrope font-bold truncate ${accent ? "text-emerald-600" : "text-slate-800"}`}>{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function FinLine({ label, value, bold, accent }) {
  const isNeg = value < 0;
  return (
    <div className="flex justify-between items-center py-0.5">
      <span className={`text-xs font-ibm ${bold ? "font-semibold text-slate-800" : "text-slate-600"}`}>{label}</span>
      <span className={`text-xs font-ibm-mono ${bold ? "font-bold" : "font-medium"} ${accent ? "text-[#1e3a8a]" : isNeg ? "text-red-600" : "text-slate-800"}`}>
        {fmt(value)}
      </span>
    </div>
  );
}

function RatioItem({ label, value }) {
  return (
    <div>
      <p className="text-[10px] text-slate-400 font-ibm">{label}</p>
      <p className="text-sm font-ibm-mono font-semibold text-slate-800">{value}</p>
    </div>
  );
}

function MetricRow({ label, value, accent, danger, bold }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-xs text-slate-500 font-ibm">{label}</span>
      <span className={`text-sm font-ibm-mono ${bold ? "font-bold" : "font-semibold"} ${accent ? "text-emerald-600" : danger ? "text-red-500" : "text-slate-800"}`}>{value}</span>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="py-10 text-center">
      <PieChart className="w-8 h-8 text-slate-300 mx-auto mb-2" />
      <p className="text-sm text-slate-400 font-ibm">{message}</p>
    </div>
  );
}

function AddPeriodForm({ businessId, onSuccess }) {
  const [form, setForm] = useState({
    period: "2025-Q1", revenue: "", cost_of_revenue: "", operating_expenses: "",
    depreciation: "", interest_expense: "", tax_expense: "",
    total_assets: "", current_assets: "", cash_equivalents: "", accounts_receivable: "",
    inventory: "", fixed_assets: "", total_liabilities: "", current_liabilities: "",
    long_term_debt: "", total_equity: "",
    operating_cash_flow: "", investing_cash_flow: "", financing_cash_flow: "",
    total_customers: "", new_customers: "", cac: "", mrr: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handle = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (k !== "period" && payload[k] !== "") payload[k] = parseFloat(payload[k]) || 0;
        if (k !== "period" && payload[k] === "") payload[k] = 0;
      });
      payload.total_customers = parseInt(payload.total_customers) || 0;
      payload.new_customers = parseInt(payload.new_customers) || 0;
      await axios.post(`${API}/businesses/${businessId}/financials`, payload, { withCredentials: true });
      toast.success("Financial period added");
      onSuccess();
    } catch { toast.error("Failed to add period"); }
    setSubmitting(false);
  };

  return (
    <ScrollArea className="max-h-[65vh] pr-3">
      <form onSubmit={handle} className="space-y-4 pb-2">
        <div>
          <Label className="text-xs font-ibm font-semibold">Period</Label>
          <Select value={form.period} onValueChange={(v) => set("period", v)}>
            <SelectTrigger data-testid="period-input" className="h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["2024-Q1", "2024-Q2", "2024-Q3", "2024-Q4", "2025-Q1", "2025-Q2", "2025-Q3", "2025-Q4"].map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <p className="text-[10px] text-slate-400 uppercase tracking-wider font-bold pt-2">Income Statement</p>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Revenue" value={form.revenue} onChange={(v) => set("revenue", v)} testId="fp-revenue" />
          <FormField label="Cost of Revenue" value={form.cost_of_revenue} onChange={(v) => set("cost_of_revenue", v)} testId="fp-cogs" />
          <FormField label="Operating Expenses" value={form.operating_expenses} onChange={(v) => set("operating_expenses", v)} testId="fp-opex" />
          <FormField label="Depreciation" value={form.depreciation} onChange={(v) => set("depreciation", v)} testId="fp-dep" />
          <FormField label="Interest Expense" value={form.interest_expense} onChange={(v) => set("interest_expense", v)} testId="fp-interest" />
          <FormField label="Tax Expense" value={form.tax_expense} onChange={(v) => set("tax_expense", v)} testId="fp-tax" />
        </div>

        <p className="text-[10px] text-slate-400 uppercase tracking-wider font-bold pt-2">Balance Sheet</p>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Total Assets" value={form.total_assets} onChange={(v) => set("total_assets", v)} testId="fp-assets" />
          <FormField label="Current Assets" value={form.current_assets} onChange={(v) => set("current_assets", v)} testId="fp-ca" />
          <FormField label="Total Liabilities" value={form.total_liabilities} onChange={(v) => set("total_liabilities", v)} testId="fp-liab" />
          <FormField label="Current Liabilities" value={form.current_liabilities} onChange={(v) => set("current_liabilities", v)} testId="fp-cl" />
          <FormField label="Total Equity" value={form.total_equity} onChange={(v) => set("total_equity", v)} testId="fp-equity" />
          <FormField label="Fixed Assets" value={form.fixed_assets} onChange={(v) => set("fixed_assets", v)} testId="fp-fa" />
        </div>

        <p className="text-[10px] text-slate-400 uppercase tracking-wider font-bold pt-2">Cash Flow</p>
        <div className="grid grid-cols-3 gap-3">
          <FormField label="Operating CF" value={form.operating_cash_flow} onChange={(v) => set("operating_cash_flow", v)} testId="fp-ocf" />
          <FormField label="Investing CF" value={form.investing_cash_flow} onChange={(v) => set("investing_cash_flow", v)} testId="fp-icf" />
          <FormField label="Financing CF" value={form.financing_cash_flow} onChange={(v) => set("financing_cash_flow", v)} testId="fp-fcf" />
        </div>

        <p className="text-[10px] text-slate-400 uppercase tracking-wider font-bold pt-2">Customer Metrics</p>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Total Customers" value={form.total_customers} onChange={(v) => set("total_customers", v)} testId="fp-cust" />
          <FormField label="New Customers" value={form.new_customers} onChange={(v) => set("new_customers", v)} testId="fp-new-cust" />
          <FormField label="CAC ($)" value={form.cac} onChange={(v) => set("cac", v)} testId="fp-cac" />
          <FormField label="MRR ($)" value={form.mrr} onChange={(v) => set("mrr", v)} testId="fp-mrr" />
        </div>

        <Button type="submit" disabled={submitting} data-testid="fp-submit-button" className="w-full h-9 text-sm bg-[#1e3a8a] hover:bg-[#1e40af] mt-4">
          {submitting ? "Adding..." : "Add Financial Period"}
        </Button>
      </form>
    </ScrollArea>
  );
}

function FormField({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-[10px] font-ibm text-slate-500">{label}</Label>
      <Input data-testid={testId} type="number" step="any" value={value} onChange={(e) => onChange(e.target.value)} className="h-8 text-xs" placeholder="0" />
    </div>
  );
}
